"use client"

import { useState, useCallback, useRef } from "react"

interface UseVoiceProps {
  onTranscript: (text: string) => void
  enabled?: boolean
}

export function useVoice({ onTranscript, enabled = true }: UseVoiceProps) {
  const [isListening, setIsListening] = useState(false)
  const [isSupported, setIsSupported] = useState(() => {
    if (typeof window !== "undefined") {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      return !!SpeechRecognition && enabled
    }
    return false
  })
  const recognitionRef = useRef<SpeechRecognition | null>(null)

  const createRecognition = useCallback(() => {
    if (typeof window === "undefined") return null
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) return null

    const recognition = new SpeechRecognition()
    recognition.continuous = false
    recognition.interimResults = false
    recognition.lang = "en-US"
    recognition.maxAlternatives = 1

    recognition.onstart = () => {
      console.log('Speech recognition started')
      setIsListening(true)
    }

    recognition.onresult = (event) => {
      console.log('Speech recognition result:', event)
      console.log('Results length:', event.results.length)
      
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i]
        console.log(`Result ${i}:`, result)
        console.log(`Result ${i} transcript:`, result[0]?.transcript)
        
        if (result[0]?.transcript) {
          const transcript = result[0].transcript.trim()
          console.log('Final transcript:', transcript)
          onTranscript(transcript)
          setIsListening(false)
          return
        }
      }
    }

    recognition.onend = () => {
      console.log('Speech recognition ended')
      setIsListening(false)
    }

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error)
      setIsListening(false)
    }

    return recognition
  }, [onTranscript])

  const startListening = useCallback(() => {
    console.log('Starting voice recognition...')
    
    if (recognitionRef.current) {
      recognitionRef.current.abort()
    }
    
    const recognition = createRecognition()
    if (!recognition) {
      console.error('Speech recognition not supported')
      return
    }
    
    recognitionRef.current = recognition
    
    try {
      recognition.start()
    } catch (error) {
      console.error('Failed to start recognition:', error)
      setIsListening(false)
    }
  }, [createRecognition])

  const stopListening = useCallback(() => {
    console.log('Stopping voice recognition...')
    if (recognitionRef.current) {
      recognitionRef.current.stop()
      recognitionRef.current = null
    }
    setIsListening(false)
  }, [])

  const speak = useCallback((text: string) => {
    if (typeof window !== "undefined" && window.speechSynthesis && enabled) {
      window.speechSynthesis.cancel()
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.rate = 0.9
      utterance.pitch = 1
      utterance.volume = 0.8
      window.speechSynthesis.speak(utterance)
    }
  }, [enabled])

  return {
    isListening,
    isSupported,
    startListening,
    stopListening,
    speak,
  }
}